/**
 * The Business Hub's getting-started path, in the order a business actually
 * needs it: say who you are → add what you sell → shape how customers see
 * it → go live → know where customers show up. Each step points to exactly
 * one module, so the path doubles as a tour of what each module is for.
 */
export type SetupStepId = 'details' | 'catalogue' | 'shop' | 'publish' | 'leads'

/** 'catalogue' resolves to the business type's own label (Fleet, Menu…). */
export type HubTarget = 'Settings' | 'catalogue' | 'My Shop' | 'Leads'

export interface SetupStep {
  id: SetupStepId
  title: string
  /** What the merchant does here, in one sentence. */
  what: string
  /** Why it matters — what it unlocks for their customers. */
  why: string
  target: HubTarget
  cta: string
  minutes: number
}

export const setupSteps: SetupStep[] = [
  {
    id: 'details',
    title: 'Confirm your business details',
    what: 'Your name, logo, contact number and locations.',
    why: 'Customers see these on your shop, and Talab uses them to answer “where are you?” and “are you open?”.',
    target: 'Settings',
    cta: 'Open business details',
    minutes: 2,
  },
  {
    id: 'catalogue',
    title: 'Add what you sell',
    what: 'Products, services or listings — one by one, from a spreadsheet, or from your website.',
    why: 'Talab only recommends what is here, so this is where it learns your prices, stock and options.',
    target: 'catalogue',
    cta: 'Go to catalogue',
    minutes: 5,
  },
  {
    id: 'shop',
    title: 'Make your shop look like you',
    what: 'Pick your colours, write your welcome line and choose what the assistant says first.',
    why: 'This is the page customers land on when you share your link.',
    target: 'My Shop',
    cta: 'Customize my shop',
    minutes: 3,
  },
  {
    id: 'publish',
    title: 'Go live and share your link',
    what: 'Publish your shop, then send the link on WhatsApp, Instagram or your website.',
    why: 'Until you publish, only you can see your shop.',
    target: 'My Shop',
    cta: 'Publish my shop',
    minutes: 1,
  },
  {
    id: 'leads',
    title: 'See where customers show up',
    what: 'Every question a customer asks in your chat arrives here with what they want and how to reach them.',
    why: 'This is where conversations turn into sales — check it daily.',
    target: 'Leads',
    cta: 'Open leads',
    minutes: 1,
  },
]

/**
 * One line per module, shown the first time a merchant opens it: what the
 * page is and where it sits in the business. Keyed by tab name; the
 * catalogue tab is looked up under 'catalogue'.
 */
export const moduleIntros: Record<string, { title: string; body: string }> = {
  catalogue: {
    title: 'Everything you sell, rent or offer',
    body: 'Talab’s assistant only recommends what is in here — so the more complete each item is, the better it answers customers.',
  },
  Leads: {
    title: 'Customers who asked about something',
    body: 'When someone asks in your shop or website chat, they arrive here with what they want, their budget and how to reach them. Follow up to turn them into a sale.',
  },
  Customers: {
    title: 'People who have connected with your business',
    body: 'Anyone who signed in to your shop or chatted with you. Only you can see your customers — never other businesses on Talab.',
  },
  Orders: {
    title: 'Purchases and bookings customers completed',
    body: 'Start an order when you begin working on it, mark it fulfilled when it is done, and record cash when you collect it.',
  },
  'My Shop': {
    title: 'How customers see your business',
    body: 'Your Talab shop page and the chat you can add to your own website. Customize either, then publish when you are ready.',
  },
  Settings: {
    title: 'Your business, team and plan',
    body: 'Business details customers see, who on your team can help, how you get paid, and your subscription.',
  },
}
